import React from 'react';
import {
  View,
  Text,
  ScrollView,
  Pressable,
  StyleSheet,
  FlatList,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { LinearGradient } from 'expo-linear-gradient';
import {
  Paprika,
  PaprikaTypography,
  PaprikaShadows,
  PaprikaZones,
  PaprikaRadius,
} from '../../styles/theme';
import Feather from '@expo/vector-icons/Feather';
import { PantryLocation } from '../../utils/pantryLocation';
import { ZONE_KEYS, ZONE_META } from './zoneMeta';
import { PantryIcon } from './PantryIcon';
import { FoodTile } from './FoodTile';
import { FreshnessPill } from './FreshnessPill';
import { PantryItemView } from '../../redux/pantrySelectors';
import { buildPreviewItems } from './previewItems';
import { HeaderAvatarButton } from '../HeaderAvatarButton';
import { useSelector } from 'react-redux';
import { selectHasAccount } from '../../redux/authSlice';

interface Props {
  onReceipt: () => void;
  onPhotoZone: (zone: PantryLocation) => void;
  onManual: (zone: PantryLocation) => void;
  onSeed: () => void;
  desktop?: boolean;
}

export function EmptyState({
  onReceipt,
  onPhotoZone,
  onManual,
  onSeed,
  desktop = false,
}: Props) {
  const { t, i18n } = useTranslation();
  // A guest is told up front that scanning needs an account (#775); the tap
  // itself opens the sign-up sheet, never the camera.
  const hasAccount = useSelector(selectHasAccount);

  // Built here, never passed in: a preview of a stocked pantry is by
  // definition not the user's pantry, and the old `previewItems` prop was fed
  // the live snapshot — which is empty whenever this screen is mounted (#624).
  const previewItems = React.useMemo(
    () => buildPreviewItems(i18n.language),
    [i18n.language],
  );

  if (desktop) {
    return (
      <DesktopEmptyState
        onReceipt={onReceipt}
        onSeed={onSeed}
        onPhotoZone={onPhotoZone}
        onManual={() => onManual('fridge')}
        previewItems={previewItems}
      />
    );
  }

  return (
    <View style={styles.root}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Header — carries the settings avatar too, so Settings stays
            reachable from this tab when the pantry is still empty (#531). */}
        <View style={styles.header}>
          <Text style={styles.title}>{t('pantry.title')}</Text>
          <HeaderAvatarButton />
        </View>

        {/* Hero Block */}
        <View style={styles.heroSection}>
          <View style={styles.heroIconBlock}>
            <LinearGradient
              colors={['#f8efe2', '#ecd9bf']}
              start={{ x: 0, y: 0 }}
              end={{ x: 0.34, y: 1 }}
              style={styles.heroIconGradient}
            />
            <View style={styles.heroIconContent}>
              <PantryIcon
                icon={ZONE_META.pantry.icon}
                size={52}
                color={PaprikaZones.pantry.accent}
              />
            </View>
            <View style={styles.heroIconBadge}>
              <Feather name="plus" size={18} color="#ffffff" />
            </View>
          </View>

          <Text style={styles.heroTitle}>
            {t('pantry.empty.heroTitle')}
          </Text>
          <Text style={styles.heroSub}>
            {t('pantry.empty.heroSub')}
          </Text>
        </View>

        {/* Primary CTA - Receipt */}
        <Pressable
          onPress={onReceipt}
          style={({ pressed }) => [
            styles.receiptButton,
            pressed && styles.receiptButtonPressed,
          ]}
        >
          <View style={styles.receiptIconBox}>
            <PantryIcon icon={{ lib: 'mci', name: 'receipt' }} size={24} color="#ffffff" />
          </View>
          <View style={styles.receiptContent}>
            <Text style={styles.receiptTitle}>
              {t('pantry.empty.receiptCta')}
            </Text>
            <Text style={styles.receiptSub}>
              {t(hasAccount ? 'pantry.empty.receiptCtaSub' : 'pantry.empty.receiptCtaSubGuest')}
            </Text>
          </View>
          <Feather
            name="chevron-right"
            size={20}
            color="rgba(255,255,255,0.7)"
          />
        </Pressable>

        {/* Secondary - Photo zones */}
        <Text style={styles.orPhotographLabel}>
          {t(hasAccount ? 'pantry.empty.orPhotograph' : 'pantry.empty.orPhotographGuest')}
        </Text>

        <View style={styles.zoneGrid}>
          <View style={styles.gridRow}>
            {ZONE_KEYS.slice(0, 2).map((zone) => (
              <PhotoZoneButton
                key={zone}
                zone={zone}
                onPress={() => onPhotoZone(zone)}
              />
            ))}
          </View>
          <View style={styles.gridRow}>
            {ZONE_KEYS.slice(2, 4).map((zone) => (
              <PhotoZoneButton
                key={zone}
                zone={zone}
                onPress={() => onPhotoZone(zone)}
              />
            ))}
          </View>
        </View>

        {/* Preview of filled spaces */}
        <Text style={styles.previewLabel}>
          {t('pantry.empty.preview')}
          <Text style={styles.previewLabelSub}> {t('pantry.empty.previewSub')}</Text>
        </Text>

        <View style={styles.previewWrap}>
          <View style={styles.previewGrid} pointerEvents="none">
            <View style={styles.gridRow}>
              {ZONE_KEYS.slice(0, 2).map((zone) => (
                <PreviewZoneCard
                  key={zone}
                  zone={zone}
                  items={previewItems[zone] || []}
                />
              ))}
            </View>
            <View style={styles.gridRow}>
              {ZONE_KEYS.slice(2, 4).map((zone) => (
                <PreviewZoneCard
                  key={zone}
                  zone={zone}
                  items={previewItems[zone] || []}
                />
              ))}
            </View>
          </View>
          <PreviewFade />
        </View>

        {/* Footer Actions */}
        <View style={styles.footerActions}>
          <Pressable
            onPress={() => onManual('fridge')}
            style={({ pressed }) => [
              styles.footerButton,
              pressed && styles.footerButtonPressed,
            ]}
          >
            <Feather name="plus" size={16} color={Paprika.textMuted} />
            <Text style={styles.footerButtonText}>
              {t('pantry.empty.manual')}
            </Text>
          </Pressable>

          <Pressable
            onPress={onSeed}
            style={({ pressed }) => [
              styles.footerButton,
              pressed && styles.footerButtonPressed,
            ]}
          >
            <Text style={[styles.footerButtonText, { color: Paprika.textMuted }]}>
              {t('pantry.empty.seed')}
            </Text>
          </Pressable>
        </View>
      </ScrollView>
    </View>
  );
}

/**
 * Photo zone button component (2x2 grid item)
 */
function PhotoZoneButton({
  zone,
  onPress,
}: {
  zone: PantryLocation;
  onPress: () => void;
}) {
  const { t } = useTranslation();
  const z = PaprikaZones[zone];
  const meta = ZONE_META[zone];

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        styles.photoZoneButton,
        { backgroundColor: z.tint, borderColor: `${z.accent}22` },
        pressed && styles.photoZonePressed,
      ]}
    >
      <View
        style={[
          styles.photoZoneIconBox,
          { backgroundColor: Paprika.glassWhite },
        ]}
      >
        <PantryIcon icon={meta.icon} size={18} color={z.accent} />
      </View>
      <Text style={[styles.photoZoneLabel, { color: Paprika.textPrimary }]}>
        {t(meta.labelKey)}
      </Text>
      <Feather name="camera" size={16} color={z.accent} />
    </Pressable>
  );
}

/**
 * Bottom fade over the preview grid, so it reads as a peek into a stocked
 * pantry rather than a section that got cut off.
 *
 * The design prototype did this with a CSS `maskImage` (gm-proto.jsx:212),
 * which React Native has no equivalent for. An overlay gradient from
 * transparent down to the page background gets the same result and works on
 * web and native alike.
 */
function PreviewFade() {
  return (
    <LinearGradient
      colors={['rgba(251,249,246,0)', Paprika.background]}
      style={styles.previewFade}
      pointerEvents="none"
    />
  );
}

/**
 * Preview zone card component (dimmed mock of filled zone)
 */
function PreviewZoneCard({
  zone,
  items,
}: {
  zone: PantryLocation;
  items: PantryItemView[];
}) {
  const { t } = useTranslation();
  const z = PaprikaZones[zone];
  const meta = ZONE_META[zone];

  return (
    <View style={[styles.previewCard, { borderColor: `${z.accent}22` }]}>
      <LinearGradient
        colors={[z.gradStart, z.gradEnd]}
        start={{ x: 0, y: 0 }}
        end={{ x: 0.34, y: 1 }}
        style={styles.previewCardHeader}
      >
        <View style={styles.previewHeaderContent}>
          <View
            style={[
              styles.previewHeaderIcon,
              { backgroundColor: Paprika.glassWhite },
            ]}
          >
            <PantryIcon icon={meta.icon} size={14} color={z.accent} />
          </View>
          <Text style={[styles.previewHeaderLabel, { color: Paprika.textPrimary }]}>
            {t(meta.labelKey)}
          </Text>
        </View>
      </LinearGradient>

      <View style={styles.previewCardItems}>
        {items.slice(0, 3).map((view) => (
          <View key={view.item.id} style={styles.previewItem}>
            <FoodTile
              category={view.item.category}
              name={view.item.name}
              size={20}
              radius={PaprikaRadius.sm}
            />
            <View style={styles.previewItemStack}>
              <Text
                style={styles.previewItemName}
                numberOfLines={1}
              >
                {view.item.name}
              </Text>
              <FreshnessPill
                state={view.state}
                days={view.days}
                compact={true}
              />
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}

/**
 * Desktop variant (simplified hero for large screens)
 */
function DesktopEmptyState({
  onReceipt,
  onSeed,
  onPhotoZone,
  onManual,
  previewItems,
}: {
  onReceipt: () => void;
  onSeed: () => void;
  onPhotoZone: (zone: PantryLocation) => void;
  onManual: () => void;
  previewItems: Record<PantryLocation, PantryItemView[]>;
}) {
  const { t } = useTranslation();
  const hasAccount = useSelector(selectHasAccount);

  return (
    <View style={styles.desktopRoot}>
      <ScrollView contentContainerStyle={styles.desktopContent}>
        <View style={styles.desktopHero}>
          <Text style={styles.desktopHeroTitle}>
            {t('pantry.empty.heroTitle')}
          </Text>
          <Text style={styles.desktopHeroSub}>
            {t('pantry.empty.heroSub')}
          </Text>

          <View style={styles.desktopCTAs}>
            <Pressable
              onPress={onReceipt}
              style={({ pressed }) => [
                styles.desktopPrimaryButton,
                pressed && styles.desktopButtonPressed,
              ]}
            >
              <PantryIcon icon={{ lib: 'mci', name: 'receipt' }} size={20} color={Paprika.toastText} />
              <Text style={styles.desktopPrimaryButtonText}>
                {t('pantry.empty.receiptCta')}
              </Text>
              {!hasAccount && (
                <Feather
                  name="lock"
                  size={14}
                  color={Paprika.toastText}
                  accessibilityLabel={t('scanGate.lockedHint')}
                  testID="scan-locked-hint"
                />
              )}
            </Pressable>

            <Pressable
              onPress={onManual}
              style={({ pressed }) => [
                styles.desktopSecondaryButton,
                pressed && styles.desktopButtonPressed,
              ]}
            >
              <Feather name="plus" size={16} color={Paprika.textSecondary} />
              <Text style={styles.desktopSecondaryButtonText}>
                {t('pantry.empty.manual')}
              </Text>
            </Pressable>
          </View>
        </View>

        {/* Or photograph a space */}
        <View style={styles.desktopPhotoRow}>
          <Text style={styles.desktopPhotoLabel}>
            {t(hasAccount ? 'pantry.empty.orPhotograph' : 'pantry.empty.orPhotographGuest')}
          </Text>
          {ZONE_KEYS.map((zone) => {
            const z = PaprikaZones[zone];
            const meta = ZONE_META[zone];
            return (
              <Pressable
                key={zone}
                onPress={() => onPhotoZone(zone)}
                accessibilityLabel={t(meta.labelKey)}
                style={({ pressed }) => [
                  styles.desktopPhotoZone,
                  { backgroundColor: z.tint, borderColor: `${z.accent}33` },
                  pressed && styles.desktopButtonPressed,
                ]}
              >
                <PantryIcon icon={meta.icon} size={18} color={z.accent} />
              </Pressable>
            );
          })}
        </View>

        {/* Dimmed preview of the 4 filled spaces */}
        <Text style={styles.desktopPreviewLabel}>
          {t('pantry.empty.preview')}
          <Text style={styles.previewLabelSub}> {t('pantry.empty.previewSub')}</Text>
        </Text>
        <View style={[styles.previewWrap, styles.desktopPreviewWrap]}>
          <View style={styles.desktopPreviewRow} pointerEvents="none">
            {ZONE_KEYS.map((zone) => (
              <View key={zone} style={styles.desktopPreviewCol}>
                <PreviewZoneCard zone={zone} items={previewItems[zone] || []} />
              </View>
            ))}
          </View>
          <PreviewFade />
        </View>

        <Pressable
          onPress={onSeed}
          style={({ pressed }) => [
            styles.desktopSeedLink,
            pressed && styles.footerButtonPressed,
          ]}
        >
          <Text style={[styles.footerButtonText, { color: Paprika.textMuted }]}>
            {t('pantry.empty.seedDesktop')}
          </Text>
        </Pressable>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 40,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 8,
    paddingBottom: 24,
  },
  title: {
    ...PaprikaTypography.headingLarge,
    color: Paprika.textPrimary,
  },

  // Hero section
  heroSection: {
    alignItems: 'center',
    marginBottom: 28,
    paddingHorizontal: 8,
  },
  heroIconBlock: {
    position: 'relative',
    width: 96,
    height: 96,
    marginBottom: 18,
  },
  heroIconGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: PaprikaRadius.md,
    borderWidth: 1,
    borderColor: `${PaprikaZones.pantry.accent}22`,
  },
  heroIconContent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  heroIconBadge: {
    position: 'absolute',
    bottom: -8,
    right: -8,
    width: 36,
    height: 36,
    borderRadius: 999,
    backgroundColor: Paprika.accent,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 3,
    borderColor: Paprika.background,
  },
  heroTitle: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 24,
    color: Paprika.textPrimary,
    lineHeight: 32,
    textAlign: 'center',
    marginBottom: 8,
    fontWeight: '400',
  },
  heroSub: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textMuted,
    lineHeight: 20,
    textAlign: 'center',
    maxWidth: 280,
  },

  // Receipt button
  receiptButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: Paprika.accent,
    borderRadius: PaprikaRadius.lg,
    marginBottom: 22,
    ...PaprikaShadows.card,
  },
  receiptButtonPressed: {
    transform: [{ scale: 0.98 }],
  },
  receiptIconBox: {
    width: 44,
    height: 44,
    borderRadius: PaprikaRadius.md,
    backgroundColor: 'rgba(255,255,255,0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  receiptContent: {
    flex: 1,
  },
  receiptTitle: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 15,
    color: '#ffffff',
    lineHeight: 20,
  },
  receiptSub: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: 'rgba(255,255,255,0.8)',
    lineHeight: 16,
    marginTop: 2,
  },

  // Or photograph label
  orPhotographLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    color: Paprika.textMuted,
    lineHeight: 16,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 12,
  },

  // Zone grid (photo buttons)
  zoneGrid: {
    marginBottom: 24,
  },
  gridRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 10,
  },
  photoZoneButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 13,
    paddingVertical: 13,
    borderRadius: PaprikaRadius.lg,
    borderWidth: 1,
  },
  photoZonePressed: {
    transform: [{ scale: 0.97 }],
  },
  photoZoneIconBox: {
    width: 34,
    height: 34,
    borderRadius: PaprikaRadius.md,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  photoZoneLabel: {
    flex: 1,
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
    lineHeight: 18,
  },

  // Preview section
  previewLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    color: Paprika.textMuted,
    lineHeight: 16,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 12,
  },
  previewLabelSub: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textMuted,
    textTransform: 'none',
    letterSpacing: 0,
  },
  previewWrap: {
    position: 'relative',
    marginBottom: 24,
  },
  // `desktopContent` centers its children, so an unsized wrapper collapses to
  // min-content and squeezes the four flex:1 columns. Stretch it back out.
  desktopPreviewWrap: {
    alignSelf: 'stretch',
  },
  previewFade: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '38%',
  },
  previewGrid: {
    opacity: 0.55,
  },
  previewCard: {
    flex: 1,
    borderRadius: PaprikaRadius.lg,
    borderWidth: 1,
    overflow: 'hidden',
    backgroundColor: Paprika.cardBg,
  },
  previewCardHeader: {
    paddingVertical: 9,
    paddingHorizontal: 11,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  previewHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  previewHeaderIcon: {
    width: 24,
    height: 24,
    borderRadius: PaprikaRadius.sm,
    justifyContent: 'center',
    alignItems: 'center',
  },
  previewHeaderLabel: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 13,
    lineHeight: 18,
  },
  previewCardItems: {
    paddingHorizontal: 8,
    paddingVertical: 8,
    gap: 4,
  },
  previewItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 4,
    paddingHorizontal: 4,
  },
  /**
   * Name on top, freshness pill under it.
   *
   * The prototype put them side by side, but it was drawn with French labels
   * ("3 mo"). The real ones run to "~3 months" and "longue conserve", which at
   * two cards per row on a 393px screen leaves ~37px for the name — enough to
   * cut "Chicken breasts" to "Chicke…". Stacking gives the name the full card
   * width in both layouts and both languages.
   */
  previewItemStack: {
    flex: 1,
    minWidth: 0,
    gap: 3,
    alignItems: 'flex-start',
  },
  previewItemName: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    color: Paprika.textPrimary,
    lineHeight: 16,
  },

  // Footer actions
  footerActions: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 18,
    marginTop: 16,
  },
  footerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  footerButtonPressed: {
    transform: [{ scale: 0.96 }],
  },
  footerButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
    color: Paprika.textMuted,
    lineHeight: 18,
  },

  // Desktop variant
  desktopRoot: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  desktopContent: {
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100%',
    paddingHorizontal: 40,
    paddingVertical: 60,
  },
  desktopHero: {
    maxWidth: 600,
    alignItems: 'center',
  },
  desktopHeroTitle: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 40,
    color: Paprika.textPrimary,
    lineHeight: 48,
    textAlign: 'center',
    marginBottom: 16,
    fontWeight: '400',
  },
  desktopHeroSub: {
    fontFamily: 'Inter-Regular',
    fontSize: 16,
    color: Paprika.textMuted,
    lineHeight: 24,
    textAlign: 'center',
    marginBottom: 40,
  },
  // `center`, not the default `stretch`: the two buttons size to their own
  // content instead of the taller one dragging the other up with it.
  desktopCTAs: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  desktopPrimaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 24,
    paddingVertical: 14,
    backgroundColor: Paprika.accent,
    borderRadius: PaprikaRadius.xl,
    ...PaprikaShadows.card,
  },
  /**
   * Mirrors `desktopPrimaryButton`'s box exactly — same radius, same padding,
   * same row layout. The row layout is not optional: without it the `+` icon
   * wraps above the label and left-aligns, and because the CTA row was also
   * defaulting to `stretch`, the primary button then inflated to match the
   * broken secondary's doubled height. Shipped that way in #475.
   *
   * Floating white paper rather than an outlined box: DESIGN.md's no-line rule
   * asks for a tonal surface shift, and `surfaceLowest` on `background` gives
   * the lift a 1px border was standing in for.
   */
  desktopSecondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: PaprikaRadius.xl,
    backgroundColor: Paprika.surfaceLowest,
    ...PaprikaShadows.card,
  },
  desktopButtonPressed: {
    transform: [{ scale: 0.98 }],
  },
  desktopPrimaryButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 15,
    color: Paprika.toastText,
    lineHeight: 20,
  },
  // Warm brown, not the cold near-black of a heading: a secondary action
  // should not carry the same weight as the primary sitting beside it.
  desktopSecondaryButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 15,
    color: Paprika.textSecondary,
    lineHeight: 20,
  },
  desktopPhotoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginTop: 28,
    alignSelf: 'center',
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: PaprikaRadius.full,
    paddingVertical: 8,
    paddingHorizontal: 16,
    ...PaprikaShadows.card,
  },
  desktopPhotoLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12.5,
    color: Paprika.textSecondary,
    marginRight: 4,
  },
  desktopPhotoZone: {
    width: 34,
    height: 34,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopPreviewLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 10.5,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: Paprika.textMuted,
    marginTop: 32,
    marginBottom: 12,
  },
  desktopPreviewRow: {
    flexDirection: 'row',
    gap: 14,
    opacity: 0.55,
  },
  desktopPreviewCol: {
    flex: 1,
    minWidth: 0,
  },
  desktopSeedLink: {
    alignSelf: 'center',
    marginTop: 20,
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
});
